class FacetValueCount {
  // Each option's count is its own section request. Firing them all at once
  // (about 20) starves the request that resolves the main "X of N" count, so
  // they run through a small queue instead. Entries from a superseded init()
  // are dropped without being fetched.
  static resolveElement(element) {
    const url = element.dataset.countUrl;
    if (!url) return Promise.resolve(null);

    const cached = VariantCardCount.getCachedUrlTotal(url, { maxPages: this.MAX_PAGES });
    if (cached) return cached;

    const requestId = element.dataset.countRequestId;
    return new Promise((resolve) => {
      this.queue.push({ element, requestId, url, resolve });
      this.pump();
    });
  }

  static pump() {
    while (this.active < this.CONCURRENCY && this.queue.length) {
      const task = this.queue.shift();
      if (task.element.dataset.countRequestId !== task.requestId) {
        task.resolve(null);
        continue;
      }

      this.active += 1;
      VariantCardCount.resolveUrlTotal(task.url, { maxPages: this.MAX_PAGES })
        .then(task.resolve, () => task.resolve(null))
        .finally(() => {
          this.active -= 1;
          this.pump();
        });
    }
  }

  // Resolves every pending count within the same checkbox list together, then
  // reveals that list's values in one frame — resolving/revealing one by one
  // makes checkboxes pop in at staggered times depending on network jitter,
  // which reads as flicker. Grouped per <ul> (one per filter, e.g. Size vs
  // Flavour) rather than the whole page, so one slow filter group's walk
  // doesn't hold up every other group's reveal too.
  static init(root = document) {
    if (!window.VariantCardCount) return;

    const elements = [...root.querySelectorAll('.facet-checkbox-count[data-count-url]')];
    if (!elements.length) return;

    // init() can be called again (a fast follow-up filter change) before an
    // earlier call's fetches resolve. Stamp every element with the call that
    // owns it, so a slower, older call's resolution can't overwrite a newer
    // one's result and make the number flicker between two values.
    const requestId = ++this.requestCounter;
    elements.forEach((element) => {
      element.dataset.countRequestId = String(requestId);
    });

    const groups = new Map();
    elements.forEach((element) => {
      const key = element.closest('ul') || element;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(element);
    });

    // Let the main count resolve first so it is not competing with these.
    const mainCountReady = (VariantCardCount.currentRefresh || Promise.resolve()).catch(() => {});

    groups.forEach((groupElements) => {
      mainCountReady.then(() => Promise.all(groupElements.map((element) => this.resolveElement(element)))).then((totals) => {
        groupElements.forEach((element, index) => {
          if (element.dataset.countRequestId !== String(requestId)) return;

          const total = totals[index];
          if (total !== null && total !== undefined) {
            element.textContent = `(${total})`;
          }

          // Reveal even on a failed fetch, falling back to the native product
          // count already in the markup rather than staying hidden.
          element.classList.remove('js-count-pending');
        });
      });
    });
  }
}

FacetValueCount.requestCounter = 0;
FacetValueCount.queue = [];
FacetValueCount.active = 0;
FacetValueCount.CONCURRENCY = 6;

// Caps how many result pages a single facet value's recount will walk — a value
// spanning many pages would otherwise trigger one fetch per page, per checkbox.
// Past the cap we show a lower-bound count rather than fetch indefinitely.
FacetValueCount.MAX_PAGES = 5;

document.addEventListener('DOMContentLoaded', () => {
  // Deferred a tick so VariantCardCount.init() has started the main count first.
  setTimeout(() => FacetValueCount.init(), 0);
});

if (typeof window !== 'undefined') {
  window.FacetValueCount = FacetValueCount;
}
